import { DateTime } from "luxon";

// User model: 1 GaimTime (compressed) hour = (60 - gain) real minutes. factor = 60/(60-gain).
export const RATIOS = { 5: 0.916, 10: 0.833, 15: 0.75, 20: 0.666 }; // legacy (backend validation only)

export function realMinPerGaimHour(gain) {
  return 60 - gain;
}

export function accelFactor(gain) {
  return 60 / (60 - gain);
}

// epoch millis of today's start_time in the given timezone
export function startEpoch(startTime, tz) {
  const [h, m] = startTime.split(":").map(Number);
  return DateTime.now().setZone(tz || undefined).set({ hour: h, minute: m, second: 0, millisecond: 0 }).toMillis();
}

// Next occurrence of the programmed start time: today if not reached yet, else tomorrow.
// Used by "Reset" (option a): counters stay at 0 until the programmed start hour.
export function nextStartEpoch(startTime, tz) {
  const [h, m] = startTime.split(":").map(Number);
  const base = DateTime.now().setZone(tz || undefined).set({ hour: h, minute: m, second: 0, millisecond: 0 });
  const target = base.toMillis() > DateTime.now().toMillis() ? base : base.plus({ days: 1 });
  return target.toMillis();
}

export function fmtEpoch(epochMs, tz) {
  return DateTime.fromMillis(epochMs).setZone(tz || undefined).toFormat("HH:mm:ss");
}

export function fmtEpochHM(epochMs, tz) {
  return DateTime.fromMillis(epochMs).setZone(tz || undefined).toFormat("HH:mm");
}

export function fmtGain(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return { h, m, s, str: `${pad(h)}:${pad(m)}:${pad(s)}` };
}

const pad = (n) => String(Math.floor(n)).padStart(2, "0");

// Planning slots: compressed (GaimTime) hour block in evidence + real time range (in tz).
// Hours wrap past midnight so night workers can plan freely.
export function buildSlots(startTime, gain, tz, count = 24) {
  const [h] = startTime.split(":").map(Number);
  const step = realMinPerGaimHour(gain);
  const s = startEpoch(startTime, tz);
  const slots = [];
  for (let i = 0; i < count; i++) {
    const gStartH = (h + i) % 24;
    const gEndH = (h + i + 1) % 24;
    const realStartMs = s + i * step * 60000;
    const realEndMs = s + (i + 1) * step * 60000;
    slots.push({
      key: `${pad(gStartH)}:00`,
      gaim: `${pad(gStartH)}h - ${pad(gEndH)}h`,
      real: `${fmtEpochHM(realStartMs, tz)} - ${fmtEpochHM(realEndMs, tz)}`,
      realStartMs,
      realEndMs,
    });
  }
  return slots;
}

// list of IANA timezones (worldwide) with graceful fallback
export function allTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") return Intl.supportedValuesOf("timeZone");
  } catch (e) { /* ignore */ }
  return [
    "Europe/Paris", "Europe/London", "Europe/Berlin", "Europe/Madrid", "Europe/Moscow",
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Sao_Paulo",
    "Africa/Casablanca", "Africa/Cairo", "Africa/Lagos", "Asia/Dubai", "Asia/Kolkata",
    "Asia/Shanghai", "Asia/Tokyo", "Asia/Singapore", "Australia/Sydney", "Pacific/Auckland", "UTC",
  ];
}

export function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Paris";
  } catch (e) {
    return "Europe/Paris";
  }
}

// GaimTime wall-clock "HH:MM" -> minutes offset from the day's start_time (0..1440, wraps).
export function gaimOffsetMin(gaimHM, startTime) {
  const [gh, gm] = gaimHM.split(":").map(Number);
  const [sh, sm] = startTime.split(":").map(Number);
  let off = gh * 60 + gm - (sh * 60 + sm);
  if (off < 0) off += 1440;
  return off;
}

// Real epoch (ms) at which the GaimTime clock shows the given "HH:MM".
export function gaimToRealMs(gaimHM, startTime, gain, tz) {
  const off = gaimOffsetMin(gaimHM, startTime);
  const factor = accelFactor(gain);
  return startEpoch(startTime, tz) + (off / factor) * 60000;
}
